import type { ReactNode } from 'react'
import { Menu } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useOptionalSidebar } from '@/components/ui/sidebar'
import { useIsMobile, useIsNarrowDesktop } from '@/hooks/useMobile'

type Props = {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  titleAdornment?: ReactNode
  className?: string
}

export function SettingsPageHeader({
  title,
  description,
  action,
  titleAdornment,
  className,
}: Props) {
  return (
    <div
      className={cn(
        'mb-7 flex flex-wrap items-start justify-between gap-3',
        className,
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <SettingsNavButton />
          <h1 className="text-[26px] font-semibold leading-tight tracking-tight">
            {title}
          </h1>
          {titleAdornment}
        </div>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action && <div className="flex flex-shrink-0 gap-2">{action}</div>}
    </div>
  )
}

/** Opens the settings nav sheet. Hidden once the sidebar is docked. */
function SettingsNavButton() {
  const { t } = useTranslation('navigation')
  const sidebar = useOptionalSidebar()
  const phone = useIsMobile()
  const narrow = useIsNarrowDesktop()
  if (!sidebar || (!phone && !narrow)) return null
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={t('openMenu')}
      onClick={() => sidebar.setOpenMobile(true)}
      className="-ml-2 inline-flex size-10 shrink-0"
    >
      <Menu className="size-5" />
    </Button>
  )
}
